import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMirrorPermissions } from '@/hooks/useMirrorPermissions';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Divider,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Typography,
  TextField,
} from '@mui/material';
import { Save, Cancel, PersonAdd } from '@mui/icons-material';

import PermissionsSection from '@/components/PermissionsSection/PermissionsSection';
import OrganizationsDropdown from '@/components/OrganizationsDropdown/OrganizationsDropdown';
import RolesDropdown from '@/components/RolesDropdown/RolesDropdown';
import FacilityPermissionsDropdown from '@/components/FacilityPermissionsDropdown/FacilityPermissionsDropdown';
import CpdActionButton from '@/components/ui/CpdActionButton';
import MirrorPermissionsDialog from './MirrorPermissionsDialog';

import { supportedLanguages } from '@/translations/config';
import FacilityAccessComponent from './FacilityAccessComponent';

const UserFormDialog = ({ open, onClose, onSave, loading = false }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    email: '',
    userName: '',
    mfa_type: 'NONE',
    languagePreference: 'en',
  });
  const [formErrors, setFormErrors] = useState({});
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [facilityPermissions, setFacilityPermissions] = useState({});
  const [organizationPermissions, setOrganizationPermissions] = useState({});
  const [applyToAllFacilities, setApplyToAllFacilities] = useState(true);
  const [selectedFacilityForConfig, setSelectedFacilityForConfig] = useState(
    '',
  );
  const [permissionsType, setPermissionsType] = useState('permission');
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [hasAllFacilitiesAccess, setHasAllFacilitiesAccess] = useState(false);
  const [roleBasedFacilitiesAccess, setRoleBasedFacilitiesAccess] = useState(
    false,
  );

  const handleHasAllFacilitiesAccessChange = (newValue) => {
    setHasAllFacilitiesAccess(newValue);
    if (newValue) {
      setSelectedFacilities([]);
    }
  };

  const handleApplyMirroredPermissions = (
    mirroredPermissions,
    sourceUser,
    permissionType,
  ) => {
    if (permissionType === 'organization_permissions') {
      setOrganizationPermissions(mirroredPermissions);
    } else if (permissionType === 'facility_permissions') {
      if (applyToAllFacilities) {
        setFacilityPermissions(mirroredPermissions);
      } else if (selectedFacilityForConfig) {
        setFacilityPermissions((prev) => ({
          ...prev,
          [selectedFacilityForConfig]: mirroredPermissions,
        }));
      }
    }
  };

  const {
    mirrorDialogOpen,
    mirrorPermissionType,
    openMirrorDialog,
    closeMirrorDialog,
    applyMirroredPermissions,
  } = useMirrorPermissions(handleApplyMirroredPermissions);

  useEffect(() => {
    if (open) {
      setFormData({
        email: '',
        userName: '',
        mfa_type: 'NONE',
        languagePreference: 'en',
      });
      setSelectedFacilities([]);
      setSelectedOrganization('');
      setFacilityPermissions({});
      setOrganizationPermissions({});
      setApplyToAllFacilities(true);
      setSelectedFacilityForConfig('');
      setPermissionsType('permission');
      setSelectedRoleId(null);
      setFormErrors({});
      setHasAllFacilitiesAccess(false);
      setRoleBasedFacilitiesAccess(false);
    }
  }, [open]);

  const handleInputChange = (e, fieldName) => {
    setFormData({ ...formData, [fieldName]: e.target.value });
    if (formErrors[fieldName]) {
      setFormErrors({ ...formErrors, [fieldName]: '' });
    }
  };

  const handleFacilityPermissionChange = (newPermissions) => {
    if (applyToAllFacilities) {
      setFacilityPermissions(newPermissions);
    } else {
      const facilityId = selectedFacilityForConfig;
      setFacilityPermissions((prev) => ({
        ...prev,
        [facilityId]: newPermissions,
      }));
    }
  };

  const handleOrganizationPermissionChange = (newPermissions) => {
    setOrganizationPermissions(newPermissions);
  };

  const handlePermissionsTypeChange = (newType) => {
    setPermissionsType(newType);
    if (newType === 'role') {
      setFacilityPermissions({});
      setOrganizationPermissions({});
    } else {
      setSelectedRoleId(null);
      handleHasAllFacilitiesAccessChange(false);
      setSelectedFacilities([]);
    }
  };

  const handleRoleChange = (roleId, hasRoleBasedFacilitiesAccess) => {
    setSelectedRoleId(roleId);
    setRoleBasedFacilitiesAccess(hasRoleBasedFacilitiesAccess);
    handleHasAllFacilitiesAccessChange(hasRoleBasedFacilitiesAccess);
  };

  const handleOrganizationChange = (organizationId) => {
    setSelectedOrganization(organizationId);
    if (organizationId) {
      setOrganizationPermissions({});
    }
  };

  const handleApplyTemplate = (template) => {
    if (template.organizationPermissions) {
      setOrganizationPermissions(template.organizationPermissions);
    }

    const templateFacilityPerms =
      template.facilityPermissions || template.permissions;

    if (templateFacilityPerms) {
      if (applyToAllFacilities) {
        setFacilityPermissions(templateFacilityPerms);
      } else {
        if (selectedFacilityForConfig) {
          setFacilityPermissions((prev) => ({
            ...prev,
            [selectedFacilityForConfig]: templateFacilityPerms,
          }));
        }
      }
    }
  };

  // INFO: Auto-select first facility for configuration when not applying to all
  useEffect(() => {
    if (
      !applyToAllFacilities &&
      selectedFacilities.length > 0 &&
      !selectedFacilityForConfig
    ) {
      const firstFacilityId = selectedFacilities[0];
      setSelectedFacilityForConfig(firstFacilityId);
    }
  }, [applyToAllFacilities, selectedFacilities, selectedFacilityForConfig]);

  const getConfiguredFacilitiesCount = () => {
    if (applyToAllFacilities) {
      return facilityPermissions && Object.keys(facilityPermissions).length > 0
        ? selectedFacilities.length
        : 0;
    } else {
      return selectedFacilities.filter((facilityId) => {
        return (
          facilityPermissions[facilityId] &&
          Object.keys(facilityPermissions[facilityId]).length > 0
        );
      }).length;
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.userName?.trim()) {
      errors.userName = 'Name is required';
    }

    if (!hasAllFacilitiesAccess && selectedFacilities.length === 0) {
      errors.facilities = 'At least one facility must be selected';
    }

    if (permissionsType === 'role' && !selectedRoleId) {
      errors.role = 'Please select a role when using role-based permissions';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const userData = {
        email: formData.email,
        name: formData.userName,
        mfaType: formData.mfa_type,
        languagePreference: formData.languagePreference,
        facilityIds: selectedFacilities.map((facilityId) =>
          parseInt(facilityId),
        ),
        organizationId: selectedOrganization
          ? parseInt(selectedOrganization)
          : null,
        permissionsType: permissionsType,
        hasAllFacilitiesAccess: hasAllFacilitiesAccess,
      };

      if (permissionsType === 'role') {
        userData.roleId = selectedRoleId;
      } else {
        const permissionsArray = [];

        if (
          selectedOrganization &&
          organizationPermissions &&
          Object.keys(organizationPermissions).length > 0
        ) {
          permissionsArray.push({
            organizationId: parseInt(selectedOrganization),
            permissions: organizationPermissions,
          });
        }

        if (applyToAllFacilities) {
          selectedFacilities.forEach((facilityId) => {
            if (
              facilityPermissions &&
              Object.keys(facilityPermissions).length > 0
            ) {
              permissionsArray.push({
                facilityId: parseInt(facilityId),
                permissions: facilityPermissions,
              });
            }
          });
        } else {
          Object.entries(facilityPermissions).forEach(
            ([facilityId, permissions]) => {
              const validFacilityId =
                facilityId && !isNaN(parseInt(facilityId))
                  ? parseInt(facilityId)
                  : null;
              if (
                validFacilityId &&
                permissions &&
                Object.keys(permissions).length > 0
              ) {
                permissionsArray.push({
                  facilityId: validFacilityId,
                  permissions: permissions,
                });
              }
            },
          );
        }

        userData.permissions = permissionsArray.filter((permission) => {
          if (permission.organizationId) {
            return true;
          }
          return (
            permission.facilityId !== null &&
            permission.facilityId !== undefined
          );
        });
      }

      await onSave(userData);
    } catch (err) {
      console.error('Error saving user:', err);
    }
  };

  const handleClose = () => {
    setFormErrors({});
    onClose();
  };

  const isFormValid =
    Object.keys(formErrors).length === 0 &&
    formData.email &&
    formData.userName &&
    (selectedFacilities.length > 0 || hasAllFacilitiesAccess);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh', maxHeight: '90vh' },
      }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAdd color='primary' />
          Create New User
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ pt: 2, overflow: 'auto', flex: 1 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant='h6' gutterBottom>
              Basic Information
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              }}>
              <TextField
                type='email'
                label={t('facilityUserManagement.modal.emailUsernameLabel')}
                value={formData.email}
                onChange={(e) => handleInputChange(e, 'email')}
                required
                size='small'
                fullWidth
                error={!!formErrors.email}
                helperText={formErrors.email}
                autoComplete='off'
              />

              <TextField
                type='text'
                label={t('facilityUserManagement.modal.nameLabel')}
                value={formData.userName}
                onChange={(e) => handleInputChange(e, 'userName')}
                required
                size='small'
                fullWidth
                error={!!formErrors.userName}
                helperText={formErrors.userName}
                autoComplete='off'
              />

              <OrganizationsDropdown
                value={selectedOrganization}
                onChange={(e) => handleOrganizationChange(e.target.value)}
                label='Organization'
                size='small'
                fullWidth
                required
              />

              <FormControl variant='outlined' size='small' fullWidth>
                <InputLabel id='language-preference'>
                  {t('facilityUserManagement.modal.languagePreferenceLabel')}
                </InputLabel>
                <Select
                  labelId='language-preference'
                  value={formData.languagePreference}
                  onChange={(e) => handleInputChange(e, 'languagePreference')}
                  label={t(
                    'facilityUserManagement.modal.languagePreferenceLabel',
                  )}>
                  {supportedLanguages.map((lang) => (
                    <MenuItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant='outlined' size='small' fullWidth>
                <InputLabel id='mfa-type'>MFA Type</InputLabel>
                <Select
                  labelId='mfa-type'
                  value={formData.mfa_type}
                  onChange={(e) => handleInputChange(e, 'mfa_type')}
                  label='MFA Type'>
                  <MenuItem value={'NONE'}>None</MenuItem>
                  <MenuItem value={'EMAIL_OTP'}>Email OTP</MenuItem>
                  <MenuItem value={'SOFTWARE_TOKEN_MFA'}>Software MFA</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {formErrors.facilities && (
              <Box sx={{ color: 'error.main', mt: 1, fontSize: '0.75rem' }}>
                {formErrors.facilities}
              </Box>
            )}
            {formErrors.role && (
              <Box sx={{ color: 'error.main', mt: 1, fontSize: '0.75rem' }}>
                {formErrors.role}
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant='h6' gutterBottom>
              Permissions Assignment
            </Typography>
            <FormControlLabel
              sx={{ ml: 0.5 }}
              control={
                <Switch
                  checked={permissionsType === 'role'}
                  onChange={(e) =>
                    handlePermissionsTypeChange(
                      e.target.checked ? 'role' : 'permission',
                    )
                  }
                  color='primary'
                />
              }
              label='Use Role-based Permissions'
            />

            {permissionsType === 'role' && (
              <Box sx={{ mb: 3, mt: 2 }}>
                {!selectedOrganization && (
                  <Alert severity='warning' sx={{ mb: 2 }}>
                    Please select an organization in the Basic Information
                    section first to see available roles.
                  </Alert>
                )}
                <RolesDropdown
                  value={selectedRoleId || ''}
                  onRoleChange={handleRoleChange}
                  label='Select Role'
                  fullWidth
                  size='small'
                  showDescription={true}
                  organizationId={selectedOrganization}
                  setRoleBasedFacilitiesAccess={setRoleBasedFacilitiesAccess}
                />
                {selectedRoleId && (
                  <Alert severity='info' sx={{ mt: 2 }}>
                    This user will inherit all permissions from the selected
                    role.
                  </Alert>
                )}
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant='h6' gutterBottom>
              Facility Assignment
            </Typography>

            <FacilityAccessComponent
              hasAllFacilitiesAccess={hasAllFacilitiesAccess}
              onHasAllFacilitiesAccessChange={
                handleHasAllFacilitiesAccessChange
              }
              roleBasedFacilitiesAccess={roleBasedFacilitiesAccess}
              selectedFacilities={selectedFacilities}
              onSelectedFacilitiesChange={setSelectedFacilities}
              selectedOrganization={selectedOrganization}
              errors={formErrors}
            />
          </Box>

          {permissionsType === 'permission' && selectedFacilities.length > 0 && (
            <>
              {selectedOrganization ? (
                <>
                  <Typography variant='h6' gutterBottom>
                    Organization Permissions
                  </Typography>
                  <Alert severity='info' sx={{ mb: 3 }}>
                    <Typography variant='body2'>
                      Permissions for the selected organization will be
                      configured below.
                    </Typography>
                  </Alert>
                  <PermissionsSection
                    permissions={organizationPermissions}
                    onPermissionChange={handleOrganizationPermissionChange}
                    onApplyTemplate={handleApplyTemplate}
                    permissionLevel='organization'
                    onMirrorClick={() =>
                      openMirrorDialog('organization_permissions')
                    }
                  />
                </>
              ) : (
                <>
                  <Typography variant='h6' gutterBottom>
                    Organization Permissions
                  </Typography>
                  <Alert severity='warning' sx={{ mb: 3 }}>
                    <Typography variant='body2'>
                      No organization selected. Please select an organization in
                      the Basic Information section to configure permissions.
                    </Typography>
                  </Alert>
                </>
              )}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Typography variant='h6'>Facility Permissions</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={applyToAllFacilities}
                        onChange={(e) => {
                          const newApplyToAll = e.target.checked;
                          setApplyToAllFacilities(newApplyToAll);
                          setFacilityPermissions({});
                        }}
                        color='primary'
                      />
                    }
                    label={`Apply to all ${selectedFacilities.length} selected facilities`}
                  />
                </Box>

                {applyToAllFacilities ? (
                  <Alert severity='info' sx={{ mb: 2 }}>
                    These permissions will be applied to all{' '}
                    {selectedFacilities.length} selected facilities.
                  </Alert>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <FacilityPermissionsDropdown
                        selectedFacilities={selectedFacilities}
                        permissions={facilityPermissions}
                        selectedFacility={selectedFacilityForConfig}
                        onFacilityChange={setSelectedFacilityForConfig}
                        organizationId={selectedOrganization}
                      />

                      <Typography variant='body2' color='text.secondary'>
                        {getConfiguredFacilitiesCount()}/
                        {selectedFacilities.length} facilities configured
                      </Typography>
                    </Box>
                  </>
                )}

                <PermissionsSection
                  permissions={
                    applyToAllFacilities
                      ? facilityPermissions
                      : facilityPermissions[selectedFacilityForConfig] || {}
                  }
                  onPermissionChange={handleFacilityPermissionChange}
                  onApplyTemplate={handleApplyTemplate}
                  permissionLevel='facility'
                  onMirrorClick={() => openMirrorDialog('facility_permissions')}
                />
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <Box sx={{ flex: 1 }} />

          <Button onClick={handleClose} startIcon={<Cancel />}>
            Cancel
          </Button>

          <CpdActionButton
            onClick={handleSave}
            loading={loading}
            disabled={!isFormValid}
            startIcon={<Save sx={{ color: 'white' }} />}>
            {loading ? 'Creating...' : 'Create User'}
          </CpdActionButton>
        </Box>
      </DialogActions>

      <MirrorPermissionsDialog
        open={mirrorDialogOpen}
        onClose={closeMirrorDialog}
        targetUser={{ name: formData.userName, email: formData.email }}
        permissionType={mirrorPermissionType}
        organizationId={selectedOrganization}
        onApplyToForm={applyMirroredPermissions}
      />
    </Dialog>
  );
};

export default UserFormDialog;
